import {
  Button,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { TInput } from '@/components/forms/form-elements/types'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import commonUtils from '@/utils/commonUtils'
import React, { useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'

interface OptionData {
  optionTitle: string
  optionDesc: string
  useYn: string
  delYn: string
}

const InsertSurvey: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()

  const { getComCodes, getComCodeValue } = useRedis()

  const { showAlert, showConfirm } = useDialog()

  const [masterData, setMasterData] = useState({
    surveyTitle: '',
    surveyDesc: '',
    surveyStartDate: '',
    surveyEndDate: '',
    isAnonymous: 'N',
    isMultiple: 'N',
    maxChoices: '',
    useYn: 'Y',
    delYn: 'N',
  })

  const [options, setOptions] = useState<OptionData[]>([])

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const name = e.target.name
    let value = e.target.value

    if (name == 'surveyDesc' && value.length > 255) {
      return
    }

    if (name == 'surveyStartDate' || name == 'surveyEndDate') {
      value = e.target.value.replace(/[^0-9]/g, '')
    }

    setMasterData((prevState) => {
      return { ...prevState, [name]: value }
    })
  }

  const handleOptionChange = (e: React.ChangeEvent<TInput>) => {
    // const name = e.target.name
    // const id = e.target.id
    // const value = e.target.value

    // const idx = Number(id.replace(name, ''))

    // setOptions((prevItems) => {
    //   const newOptions = [...prevItems]
    //   newOptions[idx] = {
    //     ...newOptions[idx],
    //     [name]: value,
    //   }
    //   return newOptions
    // })

    const name = e.target.name
    const id = e.target.id
    const value = e.target.value
    const idx = Number(id.replace(name, ''))

    // maxLength 체크 - 문자열 필드에만 적용
    const maxLengths: Record<string, number> = {
      optionTitle: 20,
      optionDesc: 150,
    }

    const maxLength = maxLengths[name]
    if (maxLength) {
      const currentValue = options[idx]?.[name as keyof (typeof options)[0]]
      const currentLength =
        typeof currentValue === 'string' ? currentValue.length : 0

      if (value.length > maxLength && value.length > currentLength) {
        showAlert({
          type: 'warning',
          title: `최대 ` + `${maxLength}` + `자까지만 입력할 수 있어요.`,
        })

        return // maxLength를 초과하고 현재보다 길어지는 경우만 막음
      }
    }

    setOptions((prevItems) => {
      const newOptions = [...prevItems]
      newOptions[idx] = {
        ...newOptions[idx],
        [name]: value,
      }
      return newOptions
    })
  }

  const deleteOption = (idx: number) => {
    if (options.length - 1 < idx || idx < 0) return
    showConfirm({
      title: '옵션을 삭제할까요?',
      onConfirm() {
        setOptions((prevItems) => prevItems.filter((_, index) => index !== idx))
      },
    })
  }

  const insertSurvey = async () => {
    // 마스터 데이터 검증
    if (masterData.surveyTitle == '') {
      showAlert({
        type: 'warning',
        title: '투표제목을 입력하세요',
        onClose() {
          document.getElementById('surveyTitle')?.focus()
        },
      })
      return
    }
    if (masterData.surveyDesc == '') {
      showAlert({
        type: 'warning',
        title: '투표설명을 입력하세요',
        onClose() {
          document.getElementById('surveyDesc')?.focus()
        },
      })
      return
    }
    if (masterData.surveyStartDate == '') {
      showAlert({
        type: 'warning',
        title: '투표시작일을 입력하세요',
        onClose() {
          document.getElementById('surveyStartDate')?.focus()
        },
      })
      return
    }
    if (masterData.surveyEndDate == '') {
      showAlert({
        type: 'warning',
        title: '투표종료일을 입력하세요',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    if (!commonUtils.isYYYYMMDD(masterData.surveyStartDate)) {
      showAlert({
        type: 'warning',
        title: '투표시작일을 확인하세요',
        onClose() {
          document.getElementById('surveyStartDate')?.focus()
        },
      })
      return
    }
    if (!commonUtils.isYYYYMMDD(masterData.surveyEndDate)) {
      showAlert({
        type: 'warning',
        title: '투표종료일을 확인하세요',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    if (Number(masterData.surveyEndDate) < Number(masterData.surveyStartDate)) {
      showAlert({
        type: 'warning',
        title: '투표종료일이 투표시작일보다 과거일 수 없습니다.',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    let optionItem = ''
    let optionItemNm = ''
    let optionIdx = -1
    // 옵션 예외처리
    options.forEach((option, idx) => {
      if (optionIdx >= 0) {
        // 이미 뭐가 문제가 있어서 패스
      } else if (option.optionTitle == '') {
        optionItem = 'optionTitle'
        optionItemNm = '옵션제목'
        optionIdx = idx
      } else if (option.optionDesc == '') {
        optionItem = 'optionDesc'
        optionItemNm = '옵션설명'
        optionIdx = idx
      }
    })

    if (optionIdx >= 0) {
      showAlert({
        type: 'warning',
        title: optionItemNm + '을 확인하세요',
        onClose() {
          document.getElementById(optionItem + optionIdx)?.focus()
        },
      })
      return
    }

    const response: any = await axios.post('/survey/insert', {
      master: masterData,
      options: options,
    })
    if (response.status === 200 && !response.data.common.resultCode) {
      // TODO 성공코드값
      navigate('/survey')
    } else {
      showAlert({
        title: response.data.common.resultMsg,
      })
    }
  }

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>투표 등록</p>
          <FlexWrapper gap="20px">
            <Button
              onClick={() =>
                showConfirm({
                  title: '투표목록 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/survey', {
                      state: { search: location.state?.search || '' },
                    })
                  },
                })
              }
              color="neverbe"
              variant="outlined"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '투표를 등록할까요?',
                  onConfirm() {
                    insertSurvey()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </FlexWrapper>
        </div>
      </div>
      <div>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyTitle">
                투표제목&nbsp;
                <span style={{ fontSize: '13px', color: 'red' }}>
                  * 60자까지 입력가능
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyTitle"
                name="surveyTitle"
                type="text"
                maxLength={60}
                value={masterData.surveyTitle}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyDesc">
                투표설명&nbsp;
                <span style={{ fontSize: '13px', color: 'red' }}>
                  * 255자까지 입력가능
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Textarea
                id="surveyDesc"
                name="surveyDesc"
                value={masterData.surveyDesc}
                height={'200px'}
                maxLength={255}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyStartDate">
                투표시작일
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyStartDate"
                name="surveyStartDate"
                type="text"
                placeholder="YYYYMMDD"
                maxLength={8}
                value={masterData.surveyStartDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyEndDate">
                투표종료일
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyEndDate"
                name="surveyEndDate"
                type="text"
                placeholder="YYYYMMDD"
                maxLength={8}
                value={masterData.surveyEndDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="isAnonymous">
                익명여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="isAnonymous"
                name="isAnonymous"
                value={masterData.isAnonymous}
                onChange={handleInputChange}
              >
                {getComCodes('IS_ANONYMOUS').map((item) => (
                  <option key={'isAnonymous' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="isMultiple">
                다중선택여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="isMultiple"
                name="isMultiple"
                value={masterData.isMultiple}
                onChange={handleInputChange}
              >
                {getComCodes('IS_MULTIPLE').map((item) => (
                  <option key={'isMultiple' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="maxChoices">
                다중선택개수
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                type="number"
                name="maxChoices"
                id="maxChoices"
                value={masterData.maxChoices}
                onChange={handleInputChange}
                disabled={masterData.isMultiple == 'N'}
                max={options.length}
                min={0}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="useYn">
                사용여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                value={masterData.useYn}
                onChange={handleInputChange}
              >
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="delYn">
                삭제여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="delYn"
                name="delYn"
                value={masterData.delYn}
                onChange={handleInputChange}
              >
                {getComCodes('DEL_YN').map((item) => (
                  <option key={'delYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginBottom: '10px',
              alignItems: 'center',
            }}
          >
            <p style={{ fontSize: '20px' }}>옵션 등록</p>
            <FlexWrapper gap="20px">
              <Button
                onClick={() => {
                  setOptions([
                    ...options,
                    {
                      optionTitle: '',
                      optionDesc: '',
                      useYn: 'Y',
                      delYn: 'N',
                    },
                  ])
                }}
                color="neverbe"
              >
                추가
              </Button>
            </FlexWrapper>
          </div>
        </div>
        <div>
          {options.map((option, idx) => {
            return (
              <React.Fragment key={`option` + idx}>
                <Row>
                  <Col md={8}>
                    <FormGroup mb="20px">
                      <Label
                        display="block"
                        mb="5px"
                        htmlFor={`optionTitle` + idx}
                      >
                        옵션제목&nbsp;
                        <span style={{ fontSize: '13px', color: 'red' }}>
                          * 8자까지 입력가능
                        </span>
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Input
                        id={`optionTitle` + idx}
                        name={`optionTitle`}
                        type="text"
                        maxLength={8}
                        value={option.optionTitle}
                        onChange={handleOptionChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup mb="20px">
                      <Label display="block" mb="5px" htmlFor={`useYn` + idx}>
                        사용여부
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Select
                        id={`useYn` + idx}
                        name={`useYn`}
                        value={option.useYn}
                        onChange={handleOptionChange}
                      >
                        {getComCodes('USE_YN').map((item) => (
                          <option key={'useYn' + item.code} value={item.code}>
                            {item.codeNm}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup mb="20px">
                      <Label display="block" mb="5px" htmlFor={`delYn` + idx}>
                        삭제여부
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Select
                        id={`delYn` + idx}
                        name={`delYn`}
                        value={option.delYn}
                        onChange={handleOptionChange}
                      >
                        {getComCodes('DEL_YN').map((item) => (
                          <option key={'delYn' + item.code} value={item.code}>
                            {item.codeNm}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={11}>
                    <FormGroup mb="20px">
                      <Label
                        display="block"
                        mb="5px"
                        htmlFor={`optionDesc` + idx}
                      >
                        옵션설명&nbsp;
                        <span style={{ fontSize: '13px', color: 'red' }}>
                          * 150자까지 입력가능
                        </span>
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Textarea
                        id={`optionDesc` + idx}
                        name={`optionDesc`}
                        maxLength={150}
                        value={option.optionDesc}
                        onChange={handleOptionChange}
                        // disabled={isMsgCdDisabled}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={1}>
                    <FormGroup mb="20px">
                      <Label htmlFor={''}>
                        <></>
                      </Label>
                      <FlexWrapper gap="10px">
                        <Button
                          onClick={() => {
                            deleteOption(idx)
                          }}
                          color="neverbe"
                          variant="outlined"
                        >
                          X
                        </Button>
                      </FlexWrapper>
                    </FormGroup>
                  </Col>
                </Row>
                <hr style={{ marginBottom: '15px' }} />
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default InsertSurvey
